import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, ChevronDown, PlusSquare, Share } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import {
  isAndroid,
  isIos,
  isMobileDevice,
  isStandalone,
  useInstallApp,
} from '@/hooks/useInstallApp'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import SetupShell, { SectionTitle } from '@/components/setup/SetupShell'
import { cn } from '@/lib/utils'

const Done: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="flex items-center gap-1.5 text-sm font-semibold text-[#4d7c0f]">
    <Check className="h-4 w-4" strokeWidth={3} /> {children}
  </p>
)

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <li className="flex items-start gap-3 text-sm text-gray-700">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
      {n}
    </span>
    <span>{children}</span>
  </li>
)

const DeviceLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">{children}</p>
)

const IphoneInstallSteps = () => (
  <ol className="space-y-2">
    <Step n={1}>
      Open Athios in Safari and tap <Share className="inline h-4 w-4 align-text-bottom" /> Share at
      the bottom.
    </Step>
    <Step n={2}>Scroll down and tap Add to Home Screen, then Add.</Step>
    <Step n={3}>Open Athios from the new icon to finish.</Step>
  </ol>
)

const AndroidInstallSteps = () => (
  <ol className="space-y-2">
    <Step n={1}>Open Athios in Chrome and tap the ⋮ menu at the top right.</Step>
    <Step n={2}>Tap Add to Home screen (or Install app), then Install.</Step>
    <Step n={3}>Open Athios from the new icon to finish.</Step>
  </ol>
)

/** A card whose heading opens and closes its steps */
const StepCard: React.FC<{
  icon: React.ReactNode
  title: string
  done: boolean
  doneLabel: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ icon, title, done, doneLabel, open, onToggle, children }) => (
  <div className="rounded-2xl border border-foreground/10 bg-card shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-2 p-4 text-left"
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-foreground">{title}</span>
        {done ? (
          <span className="mt-1 block">
            <Done>{doneLabel}</Done>
          </span>
        ) : (
          <span className="block text-xs text-gray-600">
            {open ? 'Hide steps' : 'Show me how'}
          </span>
        )}
      </span>
      <ChevronDown
        className={cn('h-5 w-5 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')}
      />
    </button>
    {open ? <div className="border-t border-foreground/10 px-4 pb-4 pt-3">{children}</div> : null}
  </div>
)

/**
 * "Get the app": add Athios to the home screen and turn on notifications. Tap a card to see how
 * for this phone (or both phones, on a computer). The install is recorded when the athlete first opens Athios from the home screen icon; notifications are on
 * once a device is subscribed. On iPhone, notifications only work after the install.
 */
const GetTheApp: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, refreshUser } = useAuth()
  const { canInstall, promptInstall } = useInstallApp()
  const { permissionState, isSubscribing, subscribe } = usePushNotifications()
  const [justInstalled, setJustInstalled] = useState(false)

  const ios = isIos()
  const android = isAndroid()
  // Installed to a computer doesn't count: this step is the phone's home screen
  const standalone = isStandalone() && isMobileDevice()
  const installed = !!athlete?.app_installed_at || standalone
  const pushOn = !!athlete?.push_enabled || permissionState === 'granted'
  const iosNeedsInstallFirst = ios && !standalone

  // Open the first unfinished card to start with
  const [open, setOpen] = useState<'install' | 'push' | null>(
    !installed ? 'install' : !pushOn ? 'push' : null,
  )
  const toggle = (card: 'install' | 'push') => setOpen((o) => (o === card ? null : card))

  const turnOnNotifications = async () => {
    const ok = await subscribe()
    if (ok) {
      await refreshUser()
      toast('Notifications on', { description: "We'll tell you when an offer comes in." })
    } else if (Notification?.permission === 'denied') {
      toast.error('Notifications are blocked', {
        description: 'Turn them on for Athios in your phone settings, then try again.',
      })
    }
  }

  return (
    <SetupShell
      title="Get the app"
      subtitle={
        <div>
          <SectionTitle>Put Athios on your phone</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            Open it like an app and hear about offers the moment they land.
          </p>
        </div>
      }
      footer={
        <Button
          variant="lime"
          className="h-12 w-full text-base"
          onClick={() => navigate('/brand-view')}
        >
          Back to your Content Shop
        </Button>
      }
      footnote="Both work on iPhone and Android."
    >
      {/* Home screen */}
      <StepCard
        icon={<PlusSquare className="h-5 w-5 shrink-0 text-foreground" />}
        title="Add Athios to your home screen"
        done={installed}
        doneLabel="On your home screen"
        open={open === 'install'}
        onToggle={() => toggle('install')}
      >
        {justInstalled ? (
          <Done>Added. Open Athios from the new icon to finish.</Done>
        ) : canInstall ? (
          <>
            <p className="text-sm text-gray-700">
              One tap, then confirm Install. The Athios icon appears on your home screen.
            </p>
            <Button
              variant="lime"
              className="mt-3 w-full"
              onClick={async () => {
                await promptInstall()
                setJustInstalled(true)
              }}
            >
              Add to home screen
            </Button>
          </>
        ) : ios ? (
          <IphoneInstallSteps />
        ) : android ? (
          <AndroidInstallSteps />
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Do this on your phone: sign in to Athios there, then follow the steps.
            </p>
            <div>
              <DeviceLabel>On iPhone</DeviceLabel>
              <IphoneInstallSteps />
            </div>
            <div>
              <DeviceLabel>On Android</DeviceLabel>
              <AndroidInstallSteps />
            </div>
          </div>
        )}
      </StepCard>

      {/* Notifications */}
      <StepCard
        icon={<Bell className="h-5 w-5 shrink-0 text-foreground" />}
        title="Turn on notifications"
        done={pushOn}
        doneLabel="Notifications are on"
        open={open === 'push'}
        onToggle={() => toggle('push')}
      >
        {iosNeedsInstallFirst ? (
          <p className="text-sm text-gray-700">
            On iPhone, notifications work once Athios is on your home screen. Add it first, then
            open Athios from the icon and turn them on here.
          </p>
        ) : permissionState === 'unsupported' ? (
          <p className="text-sm text-gray-700">
            This browser can't show notifications. Open Athios on your phone (Safari on iPhone,
            Chrome on Android) to turn them on.
          </p>
        ) : permissionState === 'denied' ? (
          <p className="text-sm text-gray-700">
            Notifications are blocked for Athios. Turn them on in your phone's settings, then come
            back here.
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              New offers, brand replies and deadlines, straight to your phone. Tap the button, then
              Allow.
            </p>
            <Button
              variant="lime"
              className="mt-3 w-full"
              disabled={isSubscribing}
              onClick={turnOnNotifications}
            >
              {isSubscribing ? 'Turning on…' : 'Turn on notifications'}
            </Button>
          </>
        )}
      </StepCard>
    </SetupShell>
  )
}

export default GetTheApp
